import { defineStore } from 'pinia';
import { createApiV2Client } from '../services/apiV2.js';
import {
    getProgramFavourites,
    localizeProgramTitle,
    toggleProgramFavourite,
} from '../components/program/programs.js';
import {
    isProgramEnabled,
    safeDisabledProgram,
} from '../components/program/programAccess.js';
import {
    isProgramSiteContextError,
    recoverProgramSiteContext,
    refreshProgramSiteAuthorization,
} from '../components/program/programSiteContext.js';

const PAGE_SIZE = 100;
const CREATE_FIELDS = ['code', 'name', 'description', 'iconKey', 'sectorIds'];
const UPDATE_FIELDS = ['name', 'description', 'iconKey', 'status', 'sectorIds'];
const ADMIN_SECTION_KEYS = ['PUBLISHED', 'DRAFT', 'ARCHIVED'];
const createAdminSection = () => ({
    programs: [],
    pagination: null,
    fetching: false,
    error: null,
    requestToken: null,
});
const createAdminSections = () =>
    Object.fromEntries(
        ADMIN_SECTION_KEYS.map((status) => [status, createAdminSection()])
    );
const adminEpochs = new WeakMap();
const accessEvents = new WeakMap();
let nextAdminListToken = 0;

function resetAdminSections(store) {
    store.adminSections = createAdminSections();
    store.adminPrograms = [];
    store.adminPagination = null;
    store.adminFetching = false;
    store.adminError = null;
    store.adminListToken = null;
}

function findAdminProgram(store, code) {
    return (
        Object.values(store.adminSections)
            .flatMap(({ programs }) => programs)
            .find((program) => program.code === code) ||
        store.adminPrograms.find((program) => program.code === code)
    );
}

let catalogConfig = {
    apiRoot: null,
    fetchImpl: undefined,
};

export function configureProgramCatalog(config = {}) {
    catalogConfig = {
        ...catalogConfig,
        ...Object.fromEntries(
            ['apiRoot', 'fetchImpl']
                .filter((field) => field in config)
                .map((field) => [field, config[field]])
        ),
    };
}

export function normalizeProgram(program, countryCode = null) {
    const source =
        program.access && !isProgramEnabled(program)
            ? safeDisabledProgram(program)
            : program;
    const code = source.code || source.hash;
    const name = source.name || source.title;
    const iconKey = source.iconKey || source.icon;
    const { sortOrder: _removedSortOrder, ...sourceWithoutSortOrder } = source;

    return {
        ...sourceWithoutSortOrder,
        code,
        name,
        iconKey,
        hash: code,
        title: localizeProgramTitle(name, countryCode),
        icon: iconKey,
        to: `/program/${code}`,
        sectorIds: Array.isArray(source.sectorIds) ? source.sectorIds : [],
        access: source.access || null,
    };
}

export function sortAndDedupePrograms(programs) {
    const byCode = new Map();
    programs.forEach((program) => {
        const normalized = normalizeProgram(program);
        if (normalized.code) byCode.set(normalized.code, normalized);
    });
    return [...byCode.values()].sort((left, right) =>
        left.code.localeCompare(right.code)
    );
}

function contractBody(source, fields) {
    return Object.fromEntries(
        fields
            .filter((field) => source[field] !== undefined)
            .map((field) => [field, source[field]])
    );
}

export function programClient() {
    return createApiV2Client({
        apiRoot: catalogConfig.apiRoot,
        fetchImpl: catalogConfig.fetchImpl || globalThis.fetch,
    });
}

function adminEpoch(store) {
    return adminEpochs.get(store) || 0;
}

function replaceCachedProgram(programs, code, replacement) {
    if (!programs.some((program) => program.code === code)) return programs;

    return sortAndDedupePrograms(
        programs.map((program) =>
            program.code === code
                ? {
                      ...program,
                      ...replacement,
                      code,
                      access: replacement.access ?? program.access,
                  }
                : program
        )
    );
}

function isEntitled(program) {
    return program.access?.state === 'ENABLED';
}

function includesAnySector(program, sectorIds) {
    if (!sectorIds.length) return true;
    const allowed = new Set(sectorIds.map(Number));
    return program.sectorIds.some((sectorId) => allowed.has(Number(sectorId)));
}

export const useProgramStore = defineStore('programs', {
    state: () => ({
        companyId: null,
        siteId: null,
        programs: [],
        loaded: false,
        fetching: false,
        fetchPromise: null,
        error: null,
        programDetails: {},
        detailFetching: false,
        detailPromise: null,
        detailRequestKey: null,
        detailError: null,
        catalogGeneration: 0,
        siteAuthorizationRefreshing: false,
        siteAuthorizationPromise: null,
        siteAuthorizationError: null,
        accessRevisions: {},
        adminSections: createAdminSections(),
        adminPrograms: [],
        adminPagination: null,
        adminFetching: false,
        adminSaving: false,
        adminError: null,
        adminListToken: null,
    }),
    getters: {
        isDynamic() {
            return true;
        },
        entitledPrograms(state) {
            return state.programs.filter(isEntitled);
        },
        programByCode: (state) => (code) =>
            state.programs.find((program) => program.code === code) || null,
        authorizedProgramByCode: (state) => (code) =>
            state.programDetails[code] || null,
        accessRevisionFor: (state) => (companyId, siteId) => {
            const company = String(companyId || '');
            const site = String(siteId || '');
            if (!company || !site) return 0;
            return (
                (state.accessRevisions[`${company}:*`] || 0) +
                (state.accessRevisions[`${company}:${site}`] || 0)
            );
        },
    },
    actions: {
        refreshSiteAuthorization({ siteStore, userStore } = {}) {
            if (!siteStore || !userStore) {
                return Promise.reject(
                    new TypeError(
                        'Program site authorization stores are required.'
                    )
                );
            }
            if (
                this.siteAuthorizationRefreshing &&
                this.siteAuthorizationPromise
            ) {
                return this.siteAuthorizationPromise;
            }

            this.siteAuthorizationRefreshing = true;
            this.siteAuthorizationError = null;
            this.siteAuthorizationPromise = refreshProgramSiteAuthorization({
                siteStore,
                userStore,
            })
                .catch((error) => {
                    this.siteAuthorizationError = error;
                    throw error;
                })
                .finally(() => {
                    this.siteAuthorizationRefreshing = false;
                    this.siteAuthorizationPromise = null;
                });

            return this.siteAuthorizationPromise;
        },
        recoverSiteContext(error, { siteStore, userStore } = {}) {
            if (!isProgramSiteContextError(error)) {
                return Promise.resolve(false);
            }
            if (!siteStore || !userStore) {
                return Promise.reject(
                    new TypeError(
                        'Program site authorization stores are required.'
                    )
                );
            }

            return recoverProgramSiteContext(
                {
                    siteStore,
                    programStore: this,
                    refreshAuthorization: () =>
                        this.refreshSiteAuthorization({ siteStore, userStore }),
                },
                error
            );
        },
        setSiteContext(siteId = null, companyId) {
            const normalizedSiteId =
                siteId === null || siteId === undefined || siteId === ''
                    ? null
                    : String(siteId);
            const normalizedCompanyId =
                companyId === undefined
                    ? this.companyId
                    : companyId === null || companyId === ''
                    ? null
                    : String(companyId);
            if (
                this.siteId === normalizedSiteId &&
                this.companyId === normalizedCompanyId
            )
                return;

            this.catalogGeneration += 1;
            this.companyId = normalizedCompanyId;
            this.siteId = normalizedSiteId;
            this.programs = [];
            this.loaded = false;
            this.fetching = false;
            this.fetchPromise = null;
            this.error = null;
            this.programDetails = {};
            this.detailFetching = false;
            this.detailPromise = null;
            this.detailRequestKey = null;
            this.detailError = null;
            this.siteAuthorizationError = null;
        },
        invalidateProgramAccess({
            companyId,
            siteId = null,
            programCode = null,
            eventKey = null,
        } = {}) {
            const company = String(companyId || '');
            const site = siteId === null ? null : String(siteId || '');
            if (!company || (siteId !== null && !site)) {
                throw new TypeError(
                    'A valid company and optional site are required.'
                );
            }

            if (eventKey) {
                const seen = accessEvents.get(this) || new Set();
                if (seen.has(eventKey)) return false;
                seen.add(eventKey);
                accessEvents.set(this, seen);
            }

            const revisionKey = `${company}:${site || '*'}`;
            this.accessRevisions = {
                ...this.accessRevisions,
                [revisionKey]: (this.accessRevisions[revisionKey] || 0) + 1,
            };

            if (
                this.companyId !== company ||
                (site !== null && this.siteId !== site)
            ) {
                return true;
            }

            const code = String(programCode || '');
            const reloadDetail = code && Boolean(this.programDetails[code]);
            this.catalogGeneration += 1;
            this.programs = [];
            this.loaded = false;
            this.fetching = false;
            this.fetchPromise = null;
            this.error = null;
            if (code) this.invalidateProgram(code);
            else this.programDetails = {};
            this.detailFetching = false;
            this.detailPromise = null;
            this.detailRequestKey = null;
            this.detailError = null;

            if (this.siteId) {
                const activeSiteId = this.siteId;
                this.load({ force: true, siteId: activeSiteId })
                    .then(() =>
                        reloadDetail
                            ? this.ensureProgram(code, activeSiteId)
                            : null
                    )
                    .catch(() => {});
            }
            return true;
        },
        ensureLoaded(siteId) {
            this.setSiteContext(siteId);
            if (!this.siteId) {
                return Promise.reject(
                    new TypeError(
                        'An authorized site must be selected to load Programs.'
                    )
                );
            }
            if (this.loaded) return Promise.resolve(this.programs);
            return this.load({ siteId: this.siteId });
        },
        refresh(siteId = this.siteId) {
            return this.load({ force: true, siteId });
        },
        load({ force = false, siteId } = {}) {
            this.setSiteContext(siteId);
            if (!this.siteId) {
                return Promise.reject(
                    new TypeError(
                        'An authorized site must be selected to load Programs.'
                    )
                );
            }
            if (this.fetching && this.fetchPromise) return this.fetchPromise;
            if (this.loaded && !force) return Promise.resolve(this.programs);

            this.fetching = true;
            const generation = this.catalogGeneration;
            const requestedSiteId = this.siteId;
            const client = programClient();

            this.fetchPromise = (async () => {
                const loadedPrograms = [];
                let page = 1;
                let totalPages = 1;

                do {
                    const response = await client.request(
                        `/sites/${encodeURIComponent(
                            requestedSiteId
                        )}/programs`,
                        {
                            query: {
                                page,
                                pageSize: PAGE_SIZE,
                            },
                        }
                    );
                    loadedPrograms.push(...(response?.items || []));
                    totalPages = Math.max(
                        1,
                        Number(response?.pagination?.totalPages) || 1
                    );
                    page += 1;
                } while (page <= totalPages);

                if (
                    this.catalogGeneration === generation &&
                    this.siteId === requestedSiteId
                ) {
                    this.programs = sortAndDedupePrograms(loadedPrograms);
                    this.loaded = true;
                    this.error = null;
                }
                return this.programs;
            })()
                .catch((error) => {
                    if (
                        this.catalogGeneration === generation &&
                        this.siteId === requestedSiteId
                    ) {
                        this.error = error;
                        if (!this.loaded) this.programs = [];
                    }
                    throw error;
                })
                .finally(() => {
                    if (
                        this.catalogGeneration === generation &&
                        this.siteId === requestedSiteId
                    ) {
                        this.fetching = false;
                        this.fetchPromise = null;
                    }
                });

            return this.fetchPromise;
        },
        ensureProgram(code, siteId) {
            this.setSiteContext(siteId);
            if (!this.siteId) {
                return Promise.reject(
                    new TypeError(
                        'An authorized site must be selected to load a Program.'
                    )
                );
            }
            if (this.programDetails[code]) {
                return Promise.resolve(this.programDetails[code]);
            }
            const catalogProgram = this.programByCode(code);
            if (catalogProgram && !isProgramEnabled(catalogProgram)) {
                this.programDetails = {
                    ...this.programDetails,
                    [code]: catalogProgram,
                };
                return Promise.resolve(catalogProgram);
            }
            const generation = this.catalogGeneration;
            const requestedSiteId = this.siteId;
            const requestKey = `${requestedSiteId}:${code}`;
            if (
                this.detailFetching &&
                this.detailRequestKey === requestKey &&
                this.detailPromise
            ) {
                return this.detailPromise;
            }
            this.detailFetching = true;
            this.detailRequestKey = requestKey;
            this.detailError = null;
            this.detailPromise = programClient()
                .request(
                    `/sites/${encodeURIComponent(
                        requestedSiteId
                    )}/programs/${encodeURIComponent(code)}`
                )
                .then((program) => {
                    if (
                        this.catalogGeneration === generation &&
                        this.siteId === requestedSiteId
                    ) {
                        this.programDetails = {
                            ...this.programDetails,
                            [code]: normalizeProgram(program),
                        };
                    }
                    return program;
                })
                .catch((error) => {
                    if (
                        this.catalogGeneration === generation &&
                        this.siteId === requestedSiteId
                    ) {
                        this.detailError = error;
                    }
                    throw error;
                })
                .finally(() => {
                    if (
                        this.catalogGeneration === generation &&
                        this.siteId === requestedSiteId &&
                        this.detailRequestKey === requestKey
                    ) {
                        this.detailFetching = false;
                        this.detailPromise = null;
                        this.detailRequestKey = null;
                    }
                });

            return this.detailPromise;
        },
        invalidateProgram(code) {
            const details = { ...this.programDetails };
            delete details[code];
            this.programDetails = details;
        },
        async listAdminPrograms({
            status,
            sectorId,
            search,
            page,
            pageSize,
        } = {}) {
            const epoch = adminEpoch(this);
            const token = ++nextAdminListToken;
            const section = ADMIN_SECTION_KEYS.includes(status)
                ? this.adminSections[status]
                : null;

            if (section) {
                section.requestToken = token;
                section.fetching = true;
                section.error = null;
            } else {
                this.adminListToken = token;
                this.adminFetching = true;
                this.adminError = null;
            }

            try {
                const response = await programClient().request('/programs', {
                    query: { status, sectorId, search, page, pageSize },
                });
                if (adminEpoch(this) !== epoch) return response;

                if (section && section.requestToken === token) {
                    section.programs = sortAndDedupePrograms(
                        response?.items || []
                    );
                    section.pagination = response?.pagination || null;
                    section.error = null;
                } else if (!section && this.adminListToken === token) {
                    this.adminPrograms = sortAndDedupePrograms(
                        response?.items || []
                    );
                    this.adminPagination = response?.pagination || null;
                    this.adminError = null;
                }
                return response;
            } catch (error) {
                if (adminEpoch(this) === epoch) {
                    if (section && section.requestToken === token)
                        section.error = error;
                    else if (!section && this.adminListToken === token)
                        this.adminError = error;
                }
                throw error;
            } finally {
                if (adminEpoch(this) === epoch) {
                    if (section && section.requestToken === token) {
                        section.fetching = false;
                        section.requestToken = null;
                    } else if (!section && this.adminListToken === token) {
                        this.adminFetching = false;
                        this.adminListToken = null;
                    }
                }
            }
        },
        async getAdminProgram(code) {
            const epoch = adminEpoch(this);
            const program = await programClient().request(
                `/programs/${encodeURIComponent(code)}`
            );
            if (adminEpoch(this) !== epoch) return program;
            this.adminPrograms = replaceCachedProgram(
                this.adminPrograms,
                code,
                program
            );
            for (const section of Object.values(this.adminSections)) {
                section.programs = replaceCachedProgram(
                    section.programs,
                    code,
                    program
                );
            }
            return program;
        },
        async createProgram(input = {}) {
            const epoch = adminEpoch(this);
            this.adminSaving = true;
            try {
                const program = await programClient().request('/programs', {
                    method: 'POST',
                    body: contractBody(input, CREATE_FIELDS),
                });

                if (adminEpoch(this) === epoch) {
                    resetAdminSections(this);
                }
                return program;
            } catch (error) {
                if (adminEpoch(this) === epoch) this.adminError = error;
                throw error;
            } finally {
                if (adminEpoch(this) === epoch) this.adminSaving = false;
            }
        },
        async patchProgram(code, input = {}, explicitVersion) {
            const cachedProgram =
                findAdminProgram(this, code) || this.programByCode(code);
            if (
                explicitVersion !== undefined &&
                (!Number.isInteger(explicitVersion) || explicitVersion <= 0)
            ) {
                throw new TypeError(
                    'Program version must be a positive integer version.'
                );
            }
            const version = explicitVersion ?? cachedProgram?.version;
            if (!Number.isInteger(version) || version <= 0) {
                throw new TypeError(
                    'Program must be loaded with a valid version before it can be updated.'
                );
            }

            const epoch = adminEpoch(this);
            this.adminSaving = true;
            try {
                const program = await programClient().request(
                    `/programs/${encodeURIComponent(code)}`,
                    {
                        method: 'PATCH',
                        body: contractBody(input, UPDATE_FIELDS),
                        ifMatch: version,
                    }
                );

                if (adminEpoch(this) === epoch) {
                    if (
                        input.status !== undefined &&
                        program.status !== cachedProgram?.status
                    ) {
                        this.catalogGeneration += 1;
                        this.programs = [];
                        this.loaded = false;
                        this.fetching = false;
                        this.fetchPromise = null;
                        this.error = null;
                    } else {
                        this.programs = replaceCachedProgram(
                            this.programs,
                            code,
                            program
                        );
                    }
                    resetAdminSections(this);
                }
                return program;
            } catch (error) {
                if (adminEpoch(this) === epoch) this.adminError = error;
                throw error;
            } finally {
                if (adminEpoch(this) === epoch) this.adminSaving = false;
            }
        },
        getPrograms({ sectorIds = [], countryCode = null } = {}) {
            const programs = this.entitledPrograms.filter((program) =>
                includesAnySector(program, sectorIds)
            );

            return sortAndDedupePrograms(programs).map((program) =>
                normalizeProgram(program, countryCode)
            );
        },
        getDiscoverablePrograms({ countryCode = null } = {}) {
            return this.programs.map((program) =>
                normalizeProgram(program, countryCode)
            );
        },
        getProgramOptions(options = {}) {
            return this.getPrograms(options).map((program) => ({
                text: program.title,
                value: program.code,
            }));
        },
        getProgramFilterOptions(options = {}) {
            return this.getPrograms(options).map((program) => ({
                label: program.title,
                value: program.code,
            }));
        },
        getProgramLabel(code, countryCode = null) {
            const program = this.programByCode(code);
            return program
                ? localizeProgramTitle(
                      program.name || program.title,
                      countryCode
                  )
                : code;
        },
        getFavouriteCodes() {
            return getProgramFavourites();
        },
        getFavouritePrograms(options = {}) {
            const favourites = new Set(this.getFavouriteCodes());
            return this.getPrograms(options).filter((program) =>
                favourites.has(program.code)
            );
        },
        isFavourite(code) {
            return getProgramFavourites().includes(code);
        },
        toggleFavourite(code) {
            return toggleProgramFavourite(code);
        },
        clear() {
            adminEpochs.set(this, adminEpoch(this) + 1);
            accessEvents.delete(this);
            this.catalogGeneration += 1;
            this.companyId = null;
            this.siteId = null;
            this.programs = [];
            this.loaded = false;
            this.fetching = false;
            this.fetchPromise = null;
            this.error = null;
            this.programDetails = {};
            this.detailFetching = false;
            this.detailPromise = null;
            this.detailRequestKey = null;
            this.detailError = null;
            this.siteAuthorizationRefreshing = false;
            this.siteAuthorizationPromise = null;
            this.siteAuthorizationError = null;
            this.accessRevisions = {};
            resetAdminSections(this);
            this.adminSaving = false;
        },
    },
});
